import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subscription, Observable } from 'rxjs';

import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import {  FieldSubmissionValueInterface, FieldSubmissionValidity } from '@core/custom-forms/models/submissions/submissions-custom-form-field-data.model';

import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';
import * as CFormsSearch from '@core/custom-forms/models/field-search.model';
import { CustomFormFieldSubmission } from '@core/objects/form';
import { CoreCustomFormsService, CoreCustomFormsFiltersService } from '@core/services/custom-forms/custom-forms.service';
import * as Objects from '@core/objects';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionListItemMenuClick } from '@core/components/versions/list/version-list-item-menu-click';

@Component({
    selector: 'form-field-fill-artwork-link-selector-v1',
    templateUrl: './fill-artwork-link-selector-v1.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FillArtworkLinkSelectorV1FieldComponent implements OnInit, OnDestroy {
    public readonly SEARCH_RESULT_STATUS = CFormsSearch.SearchHttpStatus;
    @Input() fieldSubmission: CustomFormFieldSubmission;
    @Input() dynamicFieldValues: Array<Objects.FieldFilterAssociation>;
    @Output() fieldValueChange = new EventEmitter<FieldSubmissionValueInterface>();
    @Output() fieldValidityChange = new EventEmitter<FieldSubmissionValidity>();

    public placeholder: string = "Search here";
    public term: string = "";
    public resultsReady:  boolean = false;
    public valid: boolean = false;
    public results: Array<Objects.Version> = [];
    public resultsStatus: number = this.SEARCH_RESULT_STATUS.INITIAL;
    public selectedArtwork: Objects.Version;
    public dynamicTagFilterValues: Array<number> = [];
    public staticFilterTags: Array<Objects.Label> = [];
    public localFilterTags: Array<Objects.Label> = [];
    public notMatchingWarning: boolean = false;

    // update to search delayed
    public searchControl = new FormControl();
   

    public menu: Array<ThreeDotsMenuItem> = [
        {
            title: "Select Artwork",
            key: "select",
            icon: "add",
            order: 1
        }
    ];

    public menuSelected: Array<ThreeDotsMenuItem> = [
        {
            title: "Discard Artwork",
            key: "discard",
            icon: "remove",
            order: 1
        }
    ];

    private subs = new Subscription;
    private availableLabels: Objects.Label[] = [];

    constructor(private cdr: ChangeDetectorRef,
        private filtersSrv: CoreCustomFormsFiltersService,
        private urlSrv: UrlService,
        private fileDownloadSrv: FileDownloadService,
        private signatureManager: FileSignatureManagerService,
        private customFormsSrv: CoreCustomFormsService) {}

    ngOnInit(): void {
        this.customFormsSrv.clearArtworkResults(this.fieldSubmission.field.uuid);
        this.subs.add(
            this.filtersSrv.selectLabels().subscribe((filter: CFormsSearch.SearchAvailableFilters) => {
                if(filter && filter.items?.length > 0){
                    this.availableLabels = filter.items as Objects.Label[];
                    // set filter tags from static filter in field
                    if(this.fieldSubmission.field.options.tag_filter && this.fieldSubmission.field.options.tag_filter.length > 0){
                        this.staticFilterTags = [...this.staticFilterTags,...this.availableLabels.filter((l: Objects.Label) => this.fieldSubmission.field.options.tag_filter.includes(l.id))];
                        this.cdr.markForCheck();
                    }
                    // set filter tags from dynamic fields
                    if (this.dynamicFieldValues?.length > 0){
                        this.staticFilterTags = [...this.staticFilterTags,...this.availableLabels.filter((label: Objects.Label) => 
                            this.dynamicFieldValues.some(dynamicField => 
                              dynamicField.key === 'label' && dynamicField.values.includes(label.id)
                            )
                          )];
                    }

                }
            })
        );
        // Set the value only if the provided field value is not an empty
        if (this.fieldSubmission.serialized_value) {
            this.selectedArtwork = this.fieldSubmission.serialized_value;  // Set the value without triggering the input event
            this.valid = this.fieldSubmission.serialized_value !== null || !this.fieldSubmission.field.mandatory; // Update validity
        } 
        this.emitValidity();
           
        this.subs.add(
            this.customFormsSrv.selectArtworkResults(this.fieldSubmission.field.uuid).subscribe((results: Objects.Version[]) => {
                if(results !== null && results !== undefined){
                    this.results = [...results];
                    this.initView();
                    this.cdr.markForCheck();
                }
            })
        );
        this.subs.add(
            this.customFormsSrv.selectResultStatus(this.fieldSubmission.field.uuid).subscribe((rStatus: number) => {
                this.resultsStatus = rStatus;
                this.initView();
            })
        );
        this.subs.add( this.searchControl.valueChanges.pipe(
                debounceTime(500), // Delay of 500ms after the last keypress
                distinctUntilChanged() // Only emit if the value is different than before
            ).subscribe(term => {
                if (term.trim() === '' && term !== '') {
                    return;
                }
                this.customFormsSrv.setTerm(this.fieldSubmission.field.uuid,term);
            })
        );
        // When initial (only first time) we launch first search
        this.setFilters();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.customFormsSrv.clearArtworkResults(this.fieldSubmission.field.uuid);
    }

    initView() : void {
        // TODO increase search results or way to it in the future.
        if( this.results && (this.resultsStatus === this.SEARCH_RESULT_STATUS.DONE || this.resultsStatus === this.SEARCH_RESULT_STATUS.FINAL) ){
            // we need to check if selected value is inside resutls when results done. if not clear result anyway
            if( this.selectedArtwork && 
                !this.results.some(awResult => 
                    this.selectedArtwork.id === awResult.id
                )) {
                this.notMatchingWarning = true;

                this.fieldValueChange.emit({uuid: this.fieldSubmission.field.uuid, alias: this.fieldSubmission.field.alias, value: null, type: this.fieldSubmission.field.type });
            } else {
                this.notMatchingWarning = false;
            }
            this.resultsReady = true;
            return;
        } 

        this.resultsReady = false;
    }

    onMenuClick(menu: VersionListItemMenuClick) {
        // console.debug(" menu version item  click ",menu);
        switch (menu.key) {
            case "select":
                this.selectedArtwork = menu.version;
                this.valid = true;
                if (this.valid) // type is required to use in parent components
                    this.fieldValueChange.emit({uuid: this.fieldSubmission.field.uuid, alias: this.fieldSubmission.field.alias, value: this.selectedArtwork.id, type: this.fieldSubmission.field.type });
                break;
            case "discard":
                this.setFilters();
                this.selectedArtwork = null;
                this.valid = false || !this.fieldSubmission.field.mandatory;
                this.fieldValueChange.emit({uuid: this.fieldSubmission.field.uuid, alias: this.fieldSubmission.field.alias, value: null, type: this.fieldSubmission.field.type });
                break;
        }
        this.emitValidity();
    }

    /**
     * Download file with signature validation
     * 
     * @param file the file inside artwork result or selected.
     * If file has signature (access property), use new signed download method.
     * Otherwise fall back to legacy download method.
     */
    onFileClick(file: Objects.File) {
        // Cast to AmsFile if it has access property for signature-based download
        const amsFile = file as unknown as Objects.AmsFile;
        
        // Use new signed file download if file has signature (access property)
        if (amsFile && amsFile.access) {
            // Check if signature is expired
            if (this.signatureManager.isSignatureExpired(amsFile)) {
                // Signature expired - in this search context, files are fresh from API
                // So this shouldn't happen often, but handle gracefully by falling back
                window.location.href = this.urlSrv.build('/files/'+file.id);
            } else {
                // Signature is valid, download with secure URL
                this.fileDownloadSrv.downloadVersionAsset(amsFile);
            }
        } else {
            // Fall back to legacy download method for files without signature
            window.location.href = this.urlSrv.build('/files/'+file.id);
        }
    }

    onTagSelected(tag: Objects.Label):void {
        // Check if there's already a tag with the same ID in staticFilterTags
        if (this.staticFilterTags.some(existingTag => existingTag.id === tag.id)) {
            return; // Tag is already present, so do nothing in local filters
        }
        this.localFilterTags.push(tag);
        let filters = [{ key: 'label', identifier: tag.id }];
        this.customFormsSrv.applyFilters(this.fieldSubmission.field.uuid, filters);

    }

    onRemoveFilter(tag: Objects.Label) : void {
        let filter = { key: 'label', identifier: tag.id };
        this.localFilterTags = this.localFilterTags.filter((ft:Objects.Label) => ft.id !== tag.id);
        this.customFormsSrv.removeFilter(this.fieldSubmission.field.uuid, filter);
    }

    private emitValidity(): void {
        this.fieldValidityChange.emit({ id: this.fieldSubmission.field.uuid, valid: this.fieldSubmission.field.mandatory ? this.valid :  true });
    }

    private setFilters() : void {

        let filtersToApply: CFormsSearch.SearchAppliedFilter[] = [];
        let tags_filter = this.fieldSubmission.field.options.tag_filter;

        //  set to search fitlers for each
        tags_filter.forEach(tid => {
            // this.customFormsSrv.applyFilter(this.fieldSubmission.field.uuid,{key: 'label',identifier: tid});
            filtersToApply.push({ key: 'label', identifier: tid });
        });
        if (this.fieldSubmission.field.options.status_filter){
            // this.customFormsSrv.applyFilter(this.fieldSubmission.field.uuid,{key: 'status',identifier: this.fieldSubmission.field.options.status_filter});
            filtersToApply.push({ key: 'status', identifier: this.fieldSubmission.field.options.status_filter });
        }

        // Assuming `fieldAssociations` is an array of FieldFilterAssociation
        this.dynamicFieldValues.forEach((association: Objects.FieldFilterAssociation) => {
            const { key, values } = association;
            // Check if `values` is an array
            if (Array.isArray(values)) {
                // Loop through each value in the array and call `applyFilter`
                values.forEach((value: number) => {
                    // this.customFormsSrv.applyFilter(this.fieldSubmission.field.uuid,{ key: key, identifier: value });
                    filtersToApply.push({ key: key, identifier: value });
                });
            } else if (typeof values === 'number' && values !== null) {
                // If `values` is a single number, call `applyFilter` directly
                // this.customFormsSrv.applyFilter(this.fieldSubmission.field.uuid,{ key: key, identifier: values });
                filtersToApply.push({ key: key, identifier: values });
            } else if (values === null) {
                // Handle null if necessary (e.g., log, skip, or some default behavior)
                console.log(`No values to apply for association with UUID: ${association.uuid}`);
            }
        });


        // Dispatch all filters in one call to applyFilter
        if (filtersToApply.length > 0) {
            this.customFormsSrv.applyFilters(this.fieldSubmission.field.uuid, filtersToApply);
        } else {
            // when empty filters launch first search as it is
            this.customFormsSrv.loadResults(this.fieldSubmission.field.uuid);
        }
       
    }

}
